import { useState, useMemo, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Wrench, Clock, AlertCircle, CheckCircle2, X, Plus, Pencil, Trash2, DollarSign, Loader2, FolderOpen } from 'lucide-react';
import { regularServicesData as fallbackData } from '../data/regularServicesData';
import ExcelUpload from '../components/ExcelUpload';
import apiService from '../services/api';

const RegularServicesList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    serviceName: '',
    durationMin: '',
    durationMax: '',
    priceMin: '',
    priceMax: '',
    description: '',
    signsYouNeedService: '',
    whatWeOffer: ''
  });

  // Excel upload configuration
  const excelExpectedColumns = ['Service Name', 'Duration Min', 'Duration Max', 'Price Min', 'Price Max', 'Description'];
  const excelTemplateData = [
    { 
      'Service Name': 'Oil Change', 
      'Duration Min': 30, 
      'Duration Max': 60, 
      'Price Min': 35, 
      'Price Max': 75,
      'Description': 'Replacement of engine oil and oil filter',
      'Signs You Need Service': 'Low oil warning light; Dark or dirty oil',
      'What We Offer': 'Draining old oil; Replacing oil filter; Refilling with specified oil'
    }
  ];

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new Master Regular Services Grouped API
      const response = await apiService.getMasterRegularServicesGrouped();
      if (response.success && response.data?.categories) {
        // Flatten grouped data to match our UI format
        // API returns: { categories: [{ categoryMongoId, categoryName, services: [{ id, name }] }] }
        const transformedData = [];
        response.data.categories.forEach(category => {
          (category.services || []).forEach(service => {
            transformedData.push({
              id: service.id,
              serviceName: service.name || 'Unknown',
              categoryMongoId: category.categoryMongoId,
              categoryName: category.categoryName || 'Uncategorized',
              // These fields are not in the grouped API, so we provide defaults
              durationMin: 0,
              durationMax: 0,
              priceMin: 0,
              priceMax: 0,
              description: '',
              signsYouNeedService: [],
              whatWeOffer: []
            });
          });
        });
        setData(transformedData);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching regular services:', err);
      setError('Failed to load data from server. Showing offline data.');
      // Use fallback data
      setData(fallbackData.map(item => ({ ...item })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Excel data import
  const handleExcelImport = (excelData) => {
    const maxId = Math.max(...data.map(s => typeof s.id === 'number' ? s.id : 0), 0);
    
    const newServices = excelData.map((row, index) => ({
      id: `new-${maxId + index + 1}`,
      serviceName: row['Service Name'] || row.serviceName || '',
      durationMin: parseInt(row['Duration Min'] || row.durationMin) || 0,
      durationMax: parseInt(row['Duration Max'] || row.durationMax) || 0,
      priceMin: parseFloat(row['Price Min'] || row.priceMin) || 0,
      priceMax: parseFloat(row['Price Max'] || row.priceMax) || 0,
      description: row['Description'] || row.description || '',
      signsYouNeedService: (row['Signs You Need Service'] || row.signsYouNeedService || '')
        .split(';')
        .map(s => s.trim())
        .filter(s => s.length > 0),
      whatWeOffer: (row['What We Offer'] || row.whatWeOffer || '')
        .split(';')
        .map(s => s.trim())
        .filter(s => s.length > 0)
    })).filter(s => s.serviceName);

    setData(prev => [...prev, ...newServices]);
  };

  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const categorySet = new Set(data.map(s => s.categoryName).filter(Boolean));
    return Array.from(categorySet).sort();
  }, [data]);

  // Count services per category
  const categoryCounts = useMemo(() => {
    const counts = {};
    data.forEach(service => {
      const cat = service.categoryName || 'Uncategorized';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [data]);

  // Filter data based on search and category
  const filteredData = useMemo(() => {
    let filtered = data;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(service => service.categoryName === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service => 
        service.serviceName.toLowerCase().includes(query) ||
        service.description.toLowerCase().includes(query) ||
        (service.categoryName && service.categoryName.toLowerCase().includes(query)) ||
        service.signsYouNeedService.some(sign => sign.toLowerCase().includes(query)) ||
        service.whatWeOffer.some(offer => offer.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [searchQuery, selectedCategory, data]);

  const openViewDetail = (service) => {
    setSelectedService(service);
    setIsViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedService(null);
  };

  // Open add dialog
  const openAddDialog = () => {
    setFormData({
      serviceName: '',
      durationMin: '',
      durationMax: '',
      priceMin: '',
      priceMax: '',
      description: '',
      signsYouNeedService: '',
      whatWeOffer: ''
    });
    setIsAddDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (e, service) => {
    e.stopPropagation();
    setSelectedService(service);
    setFormData({
      serviceName: service.serviceName,
      durationMin: service.durationMin?.toString() || '',
      durationMax: service.durationMax?.toString() || '',
      priceMin: service.priceMin?.toString() || '',
      priceMax: service.priceMax?.toString() || '',
      description: service.description,
      signsYouNeedService: service.signsYouNeedService.join('\n'),
      whatWeOffer: service.whatWeOffer.join('\n')
    });
    setIsEditDialogOpen(true);
  };

  // Handle add service
  const handleAddService = async () => {
    if (!formData.serviceName.trim() || !formData.description.trim()) return;
    
    setSaving(true);
    try {
      const serviceData = {
        serviceName: formData.serviceName.trim(),
        description: formData.description.trim(),
        durationObj: {
          min: parseInt(formData.durationMin) || null,
          max: parseInt(formData.durationMax) || null
        },
        costRangeObj: {
          min: parseFloat(formData.priceMin) || null,
          max: parseFloat(formData.priceMax) || null
        },
        signsYouNeedThisService: formData.signsYouNeedService
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0)
          .map(title => ({ title })),
        whatWeOffer: formData.whatWeOffer
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0)
          .map(title => ({ title }))
      };

      // Note: API requires shopMongoId - for now we'll add locally
      // In production, you'd need to pass the shop context
      const newService = {
        id: `new-${Date.now()}`,
        serviceName: formData.serviceName.trim(),
        durationMin: parseInt(formData.durationMin) || 0,
        durationMax: parseInt(formData.durationMax) || 0,
        priceMin: parseFloat(formData.priceMin) || 0,
        priceMax: parseFloat(formData.priceMax) || 0,
        description: formData.description.trim(),
        signsYouNeedService: formData.signsYouNeedService
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0),
        whatWeOffer: formData.whatWeOffer
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0)
      };
      
      setData(prev => [...prev, newService]);
      setIsAddDialogOpen(false);
    } catch (err) {
      console.error('Error adding service:', err);
      alert('Failed to add service. ' + (err.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Handle edit service
  const handleEditService = async () => {
    if (!formData.serviceName.trim() || !selectedService) return;
    
    setSaving(true);
    try {
      // Use the new Master Regular Service API - updates service name via category endpoint
      // API requires categoryId and services array: [{ serviceId, serviceName }]
      if (selectedService.id && selectedService.categoryMongoId && 
          !String(selectedService.id).startsWith('new-') && 
          selectedService.id.length === 24) {
        await apiService.updateMasterRegularServiceCategory(selectedService.categoryMongoId, {
          services: [{
            serviceId: selectedService.id,
            serviceName: formData.serviceName.trim()
          }]
        });
      }
      
      // Update local state
      setData(prev => prev.map(service => 
        service.id === selectedService.id 
          ? {
              ...service,
              serviceName: formData.serviceName.trim(),
              durationMin: parseInt(formData.durationMin) || 0,
              durationMax: parseInt(formData.durationMax) || 0,
              priceMin: parseFloat(formData.priceMin) || 0,
              priceMax: parseFloat(formData.priceMax) || 0,
              description: formData.description.trim(),
              signsYouNeedService: formData.signsYouNeedService
                .split('\n')
                .map(s => s.trim())
                .filter(s => s.length > 0),
              whatWeOffer: formData.whatWeOffer
                .split('\n')
                .map(s => s.trim())
                .filter(s => s.length > 0)
            }
          : service
      ));
      
      setIsEditDialogOpen(false);
      setSelectedService(null);
    } catch (err) {
      console.error('Error updating service:', err);
      alert('Failed to save changes. ' + (err.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Handle delete service
  const handleDeleteService = (e, serviceId) => {
    e.stopPropagation();
    const service = data.find(s => s.id === serviceId);
    if (window.confirm(`Are you sure you want to delete "${service?.serviceName}"?`)) {
      setData(prev => prev.filter(s => s.id !== serviceId));
    }
  };

  // Format duration display
  const formatDuration = (min, max) => {
    if (min && max && min !== max) {
      return `${min}-${max} min`;
    } else if (min || max) {
      return `${min || max} min`;
    }
    return 'Varies';
  };

  // Format price display
  const formatPrice = (min, max) => {
    if (min && max && min !== max) {
      return `$${min}-$${max}`;
    } else if (min || max) {
      return `$${min || max}`;
    }
    return 'Quote';
  };

  // Form dialog content
  const ServiceFormContent = () => (
    <div className="space-y-4 mt-4">
      <div>
        <Label className="text-[#94A3B8]">Service Name *</Label>
        <Input
          value={formData.serviceName}
          onChange={(e) => setFormData(prev => ({ ...prev, serviceName: e.target.value }))}
          placeholder="e.g., Oil Change"
          className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
          data-testid="service-name-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#94A3B8]">Duration Min (minutes)</Label>
          <Input
            type="number"
            value={formData.durationMin}
            onChange={(e) => setFormData(prev => ({ ...prev, durationMin: e.target.value }))}
            placeholder="e.g., 30"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="duration-min-input"
          />
        </div>
        <div>
          <Label className="text-[#94A3B8]">Duration Max (minutes)</Label>
          <Input
            type="number"
            value={formData.durationMax}
            onChange={(e) => setFormData(prev => ({ ...prev, durationMax: e.target.value }))}
            placeholder="e.g., 60"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="duration-max-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#94A3B8]">Price Min ($)</Label>
          <Input
            type="number"
            value={formData.priceMin}
            onChange={(e) => setFormData(prev => ({ ...prev, priceMin: e.target.value }))}
            placeholder="e.g., 35"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="price-min-input"
          />
        </div>
        <div>
          <Label className="text-[#94A3B8]">Price Max ($)</Label>
          <Input
            type="number"
            value={formData.priceMax}
            onChange={(e) => setFormData(prev => ({ ...prev, priceMax: e.target.value }))}
            placeholder="e.g., 75"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="price-max-input"
          />
        </div>
      </div>
      <div>
        <Label className="text-[#94A3B8]">Description *</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe the service..."
          rows={2}
          className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] resize-none"
          data-testid="description-input"
        />
      </div>
      <div>
        <Label className="text-[#94A3B8]">Signs You Need This Service</Label>
        <Textarea
          value={formData.signsYouNeedService}
          onChange={(e) => setFormData(prev => ({ ...prev, signsYouNeedService: e.target.value }))}
          placeholder="Enter each sign on a new line..."
          rows={4}
          className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] resize-none"
          data-testid="signs-input"
        />
        <p className="text-xs text-[#64748B] mt-1">Enter each sign on a new line</p>
      </div>
      <div>
        <Label className="text-[#94A3B8]">What We Offer</Label>
        <Textarea
          value={formData.whatWeOffer}
          onChange={(e) => setFormData(prev => ({ ...prev, whatWeOffer: e.target.value }))}
          placeholder="Enter each offering on a new line..."
          rows={4}
          className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] resize-none"
          data-testid="offers-input"
        />
        <p className="text-xs text-[#64748B] mt-1">Enter each offering on a new line</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
          <p className="text-[#94A3B8]">Loading regular services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="regular-services-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Regular Services Master List
          </h1>
          <p className="text-[#94A3B8]">
            Browse all available regular maintenance and repair services
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExcelUpload
            onDataParsed={handleExcelImport}
            expectedColumns={excelExpectedColumns}
            templateData={excelTemplateData}
            templateFileName="regular_services_template.xlsx"
            title="Upload Regular Services Data"
          />
          <Button 
            onClick={openAddDialog}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="add-service-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg text-[#F59E0B]">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#E2E8F0]">{filteredData.length}</p>
              <p className="text-sm text-[#64748B]">Services {selectedCategory !== 'all' ? 'in Category' : 'Available'}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-[#22C55E]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#E2E8F0]">{categories.length}</p>
              <p className="text-sm text-[#64748B]">Categories</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Category Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <Input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1E293B] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11"
            data-testid="search-input"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-[250px] bg-[#1E293B] border-[#334155] text-[#E2E8F0]" data-testid="category-filter">
            <SelectValue placeholder="Filter by Category" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-[#334155] max-h-[300px]">
            <SelectItem value="all" className="text-[#E2E8F0] focus:bg-[#334155]">
              All Categories ({data.length})
            </SelectItem>
            {categories.map(category => (
              <SelectItem key={category} value={category} className="text-[#E2E8F0] focus:bg-[#334155]">
                {category} ({categoryCounts[category] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredData.length === 0 ? (
          <div className="col-span-full text-center py-8 text-[#64748B]">
            No services found matching your search.
          </div>
        ) : (
          filteredData.map((service) => (
            <div
              key={service.id}
              className="bg-[#1E293B] border border-[#334155] rounded-lg p-5 hover:border-[#2563EB]/50 transition-colors cursor-pointer group"
              onClick={() => openViewDetail(service)}
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#EF4444]/20 flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-5 h-5 text-[#EF4444]" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => openEditDialog(e, service)}
                      className="h-7 w-7 text-[#64748B] hover:text-[#E2E8F0] hover:bg-[#334155]"
                      data-testid={`edit-service-${service.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleDeleteService(e, service.id)}
                      className="h-7 w-7 text-[#64748B] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                      data-testid={`delete-service-${service.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-[#E2E8F0] mb-2">
                {service.serviceName}
              </h3>

              {/* Category Badge */}
              {service.categoryName && (
                <Badge variant="outline" className="bg-[#2563EB]/20 text-[#60A5FA] border-[#2563EB]/30 mb-3">
                  <FolderOpen className="w-3 h-3 mr-1" />
                  {service.categoryName}
                </Badge>
              )}
              
              <p className="text-sm text-[#94A3B8] line-clamp-2 mb-4">
                {service.description || 'No description available'}
              </p>

              {/* Duration and Price Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="bg-[#334155] text-[#94A3B8] border-[#475569]">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatDuration(service.durationMin, service.durationMax)}
                </Badge>
                <Badge variant="outline" className="bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30">
                  <DollarSign className="w-3 h-3 mr-0.5" />
                  {formatPrice(service.priceMin, service.priceMax)}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748B]">
                  {service.whatWeOffer.length} offerings
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-[#2563EB] hover:text-[#60A5FA] hover:bg-[#2563EB]/10 p-0 h-auto"
                >
                  View Details
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View Service Detail Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#EF4444]/20 flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-[#EF4444]" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold text-[#E2E8F0]">
                    {selectedService?.serviceName}
                  </DialogTitle>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedService?.categoryName && (
                      <Badge variant="outline" className="bg-[#2563EB]/20 text-[#60A5FA] border-[#2563EB]/30">
                        <FolderOpen className="w-3 h-3 mr-1" />
                        {selectedService.categoryName}
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-[#334155] text-[#94A3B8] border-[#475569]">
                      <Clock className="w-3 h-3 mr-1" />
                      {selectedService && formatDuration(selectedService.durationMin, selectedService.durationMax)}
                    </Badge>
                    <Badge variant="outline" className="bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30">
                      <DollarSign className="w-3 h-3 mr-0.5" />
                      {selectedService && formatPrice(selectedService.priceMin, selectedService.priceMax)}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeViewDialog}
                className="text-[#64748B] hover:text-[#E2E8F0] hover:bg-[#334155]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </DialogHeader>

          {selectedService && (
            <div className="space-y-6 mt-4">
              {/* Description */}
              <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                <p className="text-[#94A3B8] leading-relaxed">
                  {selectedService.description}
                </p>
              </div>

              {/* Duration & Price Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-sm font-semibold text-[#E2E8F0]">Duration</span>
                  </div>
                  <p className="text-[#94A3B8]">
                    {selectedService.durationMin} - {selectedService.durationMax} minutes
                  </p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-[#22C55E]" />
                    <span className="text-sm font-semibold text-[#E2E8F0]">Price Range</span>
                  </div>
                  <p className="text-[#94A3B8]">
                    ${selectedService.priceMin} - ${selectedService.priceMax}
                  </p>
                </div>
              </div>

              {/* Signs You Need This Service */}
              {selectedService.signsYouNeedService.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#EF4444] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Signs You Need This Service
                  </h4>
                  <ul className="space-y-2">
                    {selectedService.signsYouNeedService.map((sign, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-2 h-2 rounded-full bg-[#EF4444] mt-2 flex-shrink-0"></span>
                        <span className="text-[#E2E8F0]">{sign}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What We Offer */}
              {selectedService.whatWeOffer.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#22C55E] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    What We Offer
                  </h4>
                  <ul className="space-y-2">
                    {selectedService.whatWeOffer.map((offer, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-0.5 flex-shrink-0" />
                        <span className="text-[#E2E8F0]">{offer}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#2563EB]" />
              Add New Service
            </DialogTitle>
          </DialogHeader>
          <ServiceFormContent />
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddService}
              disabled={!formData.serviceName.trim() || !formData.description.trim() || saving}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="save-service-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Service'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#F59E0B]" />
              Edit Service
            </DialogTitle>
          </DialogHeader>
          <ServiceFormContent />
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditService}
              disabled={!formData.serviceName.trim() || !formData.description.trim() || saving}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
              data-testid="update-service-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Update Service'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegularServicesList;
